import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Slider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputLabel
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../request/http-common';
import { setTradings } from '../../../request/Analysis/hitorySlice';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1E2329',
    color: 'white',
    fontSize: '12px',
    height: '32px',
    '& fieldset': { borderColor: '#2B3139' },
    '&:hover fieldset': { borderColor: '#F7A600' },
    '&.Mui-focused fieldset': { borderColor: '#F7A600' }
  },
  '& .MuiInputLabel-root': { 
    color: '#848E9C', 
    fontSize: '11px',
    '&.Mui-focused': { color: '#F7A600' }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#1E2329',
  color: 'white',
  fontSize: '12px',
  height: '32px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '& .MuiSvgIcon-root': { color: '#848E9C' },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const ChaseLimitOrder = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tradingPlans } = useSelector((state) => state.plans);
  const { user } = useSelector((state) => state.auth);
  
  const [chasePrice, setChasePrice] = useState('Bid1/Ask1');
  const [quantity, setQuantity] = useState('');
  const [maxChaseDistance, setMaxChaseDistance] = useState(false);
  const [chasingInterval, setChasingInterval] = useState('1S');
  const [reduceOnly, setReduceOnly] = useState(false);
  const [quantitySlider, setQuantitySlider] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderValue, setOrderValue] = useState(0);

  const chasePriceOptions = ['Bid1/Ask1', 'Bid2/Ask2', 'Bid3/Ask3', 'Last Price'];
  const chasingIntervalOptions = ['1S', '2S', '5S', '10S', '30S'];

  // Improved decimal formatting
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatQuantity = (value) => parseFloat(value || 0).toFixed(4);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const getSelectedPlan = () => {
    if (!tradingPlans || !Array.isArray(tradingPlans)) return null;
    return tradingPlans.find(plan => plan.id === parseInt(selectedDuration));
  };

  const selectedPlan = getSelectedPlan();

  const calculateOrderValue = () => {
    const qty = parseFloat(quantity || 0);
    const price = parseFloat(coinPrice || 0);
    return qty * price;
  };

  useEffect(() => {
    const value = calculateOrderValue();
    setOrderValue(value);
  }, [quantity, coinPrice]);

  const handleQuantitySliderChange = (event, newValue) => {
    setQuantitySlider(newValue);
    if (balance && coinPrice) {
      const maxQty = parseFloat(balance) / parseFloat(coinPrice);
      const newQuantity = (maxQty * newValue) / 100;
      setQuantity(formatQuantity(newQuantity));
    }
  };

  const validateForm = () => {
    if (!selectedDuration) {
      setError('Please select a duration');
      return false;
    }
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Please enter a valid quantity');
      return false;
    }
    if (orderValue > parseFloat(balance)) {
      setError('Order value exceeds available balance');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setShowConfirmation(true);
  };

  const confirmOrder = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const orderData = {
        user_id: user?.id,
        trading_type: tradingType,
        order_type: 'ChaseLimitOrder',
        side: orderType,
        symbol: currentPair,
        quantity: parseFloat(quantity),
        chase_price: chasePrice,
        max_chase_distance: maxChaseDistance,
        chasing_interval: chasingInterval,
        reduce_only: reduceOnly,
        duration: selectedDuration,
        duration_name: selectedPlan?.name || '',
        order_value: orderValue,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const response = await apiClient.post('/api/start-trading', orderData);

      if (response.data.status === 'success') {
        // Add to Redux store
        if (response.data.tradings) {
          dispatch(setTradings(response.data.tradings));
        }
        
        // Show success notification
        localStorage.setItem('notification', JSON.stringify({
          message: `${orderType} chase limit order placed successfully!`,
          type: 'success'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        
        // Reset form
        setQuantity('');
        setQuantitySlider(0);
        setChasePrice('Bid1/Ask1');
        setMaxChaseDistance(false);
        setChasingInterval('1S');
        setReduceOnly(false);
        setSelectedDuration('');
        setOrderValue(0);
        
        setShowConfirmation(false);
        setIsSubmitting(false);
        
        // Redirect to trade history
        navigate('/trade-history');
      } else {
        setError(response.data.message || 'Failed to place order');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Order submission error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const cancelOrder = () => {
    setShowConfirmation(false);
    setError('');
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 1.5, fontSize: '11px' }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Duration Selection */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedDuration}
            onChange={(e) => setSelectedDuration(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#1E2329',
              color: 'white',
              fontSize: '12px',
              height: '32px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
              '& .MuiSelect-icon': { color: '#848E9C' }
            }}
          >
            <MenuItem value="" disabled>
              <Typography sx={{ color: '#848E9C', fontSize: '12px' }}>
                Select Duration
              </Typography>
            </MenuItem>
            {tradingPlans && Array.isArray(tradingPlans) && tradingPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                <Typography sx={{ color: 'white', fontSize: '12px' }}>
                  {plan.name}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Cross/Isolated and Leverage */}
      {tradingType === 'Futures' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StyledSelect size="small" value="Cross" sx={{ flex: 1 }}>
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </StyledSelect>
          <StyledSelect size="small" value="10x" sx={{ flex: 1 }}>
            <MenuItem value="10x">10x</MenuItem>
            <MenuItem value="20x">20x</MenuItem>
            <MenuItem value="50x">50x</MenuItem>
            <MenuItem value="100x">100x</MenuItem>
          </StyledSelect>
        </Box>
      )}

      {/* Chase Price */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Chase Price
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={chasePrice}
            onChange={(e) => setChasePrice(e.target.value)}
          >
            {chasePriceOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
        
        {/* Current Chase Price Display */}
        <Box sx={{ mt: 0.5, fontSize: '10px', color: '#848E9C' }}>
          Bid1 {formatPrice(coinPrice * 0.9999)} / Ask1 {formatPrice(coinPrice * 1.0001)}
        </Box>
      </Box>

      {/* Quantity */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Quantity ({currentPair})
        </Typography>
        <StyledTextField
          fullWidth
          size="small"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="0.0000"
        />
      </Box>

      {/* Quantity Slider */}
      <Box sx={{ mb: 1.5, px: 0.5 }}>
        <Slider
          value={quantitySlider}
          onChange={handleQuantitySliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(value => (
            <Typography key={value} variant="caption" sx={{ color: '#848E9C', fontSize: '9px' }}>
              {value}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Max Chase Distance Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={maxChaseDistance}
            onChange={(e) => setMaxChaseDistance(e.target.checked)}
            size="small"
            sx={{ 
              color: '#848E9C',
              '&.Mui-checked': { color: '#F7A600' },
              padding: '4px'
            }}
          />
        }
        label={
          <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
            Max. Chase Distance
          </Typography>
        }
        sx={{ mb: 1.5, ml: 0 }}
      />

      {/* Chasing Interval */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Chasing Interval
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={chasingInterval}
            onChange={(e) => setChasingInterval(e.target.value)}
          >
            {chasingIntervalOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Order Summary */}
      <Box sx={{ 
        bgcolor: '#181A20', 
        borderRadius: 1, 
        p: 1, 
        mb: 1.5,
        border: '1px solid #2B3139'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Value
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {formatPrice(orderValue)} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Cost
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {formatPrice(orderValue)} USDT
          </Typography>
        </Box>
      </Box>

      {/* Reduce-Only Checkbox */}
      {tradingType === 'Futures' && (
        <FormControlLabel
          control={
            <Checkbox
              checked={reduceOnly}
              onChange={(e) => setReduceOnly(e.target.checked)}
              size="small"
              sx={{ 
                color: '#848E9C',
                '&.Mui-checked': { color: '#F7A600' },
                padding: '4px'
              }}
            />
          }
          label={
            <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
              Reduce-Only
            </Typography>
          }
          sx={{ mb: 1.5, ml: 0 }}
        />
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={orderType !== 'Buy' || isSubmitting}
          sx={{
            bgcolor: orderType === 'Buy' ? '#0ECB81' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Buy' ? '#0BB76D' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} sx={{ color: 'white' }} />
          ) : (
            `${tradingType === 'Futures' ? 'Long' : 'Buy'} ${currentPair}`
          )}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={orderType !== 'Sell' || isSubmitting}
          sx={{
            bgcolor: orderType === 'Sell' ? '#F6465D' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Sell' ? '#E13B52' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} sx={{ color: 'white' }} />
          ) : (
            `${tradingType === 'Futures' ? 'Short' : 'Sell'} ${currentPair}`
          )}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={cancelOrder}
        PaperProps={{
          sx: {
            bgcolor: '#1E2329',
            color: 'white',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2B3139', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#F7A600' }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
              Confirm {orderType} Order
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#848E9C', fontSize: '12px', mb: 1 }}>
              Order Details:
            </Typography>
            <Box sx={{ bgcolor: '#181A20', p: 1.5, borderRadius: 1, border: '1px solid #2B3139' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Type:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>Chase Limit Order</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Side:</Typography>
                <Typography sx={{ color: orderType === 'Buy' ? '#0ECB81' : '#F6465D', fontSize: '11px' }}>
                  {orderType}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Symbol:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{currentPair}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Quantity:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{quantity} {currentPair}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Chase Price:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{chasePrice}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Order Value:</Typography>
                <Typography sx={{ color: '#0ECB81', fontSize: '11px', fontWeight: 500 }}>
                  {formatPrice(orderValue)} USDT
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Duration:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>
                  {selectedPlan?.name || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>Chasing Interval:</Typography>
                <Typography sx={{ color: 'white', fontSize: '11px' }}>{chasingInterval}</Typography>
              </Box>
            </Box>
          </Box>
          <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
            Are you sure you want to place this chase limit order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2B3139', pt: 1 }}>
          <Button
            onClick={cancelOrder}
            sx={{
              color: '#848E9C',
              '&:hover': { bgcolor: 'rgba(132, 142, 156, 0.1)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmOrder}
            disabled={isSubmitting}
            sx={{
              bgcolor: orderType === 'Buy' ? '#0ECB81' : '#F6465D',
              color: 'white',
              '&:hover': {
                bgcolor: orderType === 'Buy' ? '#0BB76D' : '#E13B52'
              },
              '&:disabled': {
                bgcolor: '#2B3139',
                color: '#848E9C'
              }
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              `Confirm ${orderType}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChaseLimitOrder; 